import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { backend } from '@/api/backend';
import { explainLine } from '@/api/explain';
import { ApiError, newIdempotencyKey } from '@/api/http';
import { useCompanies } from '@/api/hooks';
import { formatCount, formatRelative } from '@/lib/format';
import { accountListingRefusal, registerBody, whyNotRegistrable } from '@/pages/registration';
import { RegisterScope } from '@/components/RegisterScope';
import { BucketFolders } from '@/components/BucketFolders';
import { ChevronRight } from 'lucide-react';
import { Button, Panel, PanelHeader, Skeleton, Stamp, Loading, ReadFailed } from '@/components/ui';

const retry = (n: number, e: unknown) => (e instanceof ApiError ? e.retryable && n < 2 : n < 2);

/**
 * The AWS account is read from what the discovery executor observed, and from
 * this company's existing registrations. There is no fallback, and there was
 * one: a literal account number, written here.
 *
 * Its own comment said the account "is read from the registrations rather than
 * written here, so a different account is a data change and not a code
 * change", and the line beneath it wrote one. It fired in exactly the case
 * where nothing could correct it - a company with no registration yet - and
 * from there it did two things. It printed "N buckets observed in account
 * 938463256739" as an observed fact. And it went into the body of the register
 * POST, so the first bucket a new account ever registered was filed under an
 * account number belonging to somewhere else.
 *
 * The account-buckets listing carries bucketName, region, timestamps and a
 * registered flag, and no account id, so when no registration exists the
 * product genuinely does not know which account it is looking at. It says so
 * rather than choosing one.
 */

/**
 * The S3 interface: every bucket the discovery identity observes in the AWS
 * account, searchable, registrable in one click. Nothing is hardcoded - the
 * listing IS the account, refreshed by discovery sweeps. Point the tool at
 * any bucket and run it.
 */
export function S3Browser() {
  const search = useRef<HTMLInputElement>(null);
  useEffect(() => {
    search.current?.focus();
  }, []);
  const companies = useCompanies(true);
  /**
   * The key that reads the account listing. Nothing is registered under it.
   *
   * A dropdown sat at the top of this page asking which company to register
   * under, which is a question about a level that does not exist: a bucket is
   * the whole entity and nothing groups one to another. That went, and the
   * value stayed in the register body, which is worse, an invisible answer to
   * the same dead question. `RegisterBucketRequest.companyId` is optional now,
   * so it is gone from there too and the server mints an owner per bucket.
   *
   * What still needs it is `GET /account-buckets`, whose `companyId` remains
   * required. It is used here as a key to that door and for nothing else; see
   * ask 8 in docs/BACKEND_ASKS.md.
   */
  const companyId = companies.data?.items[0]?.id ?? '';

  const observed = useQuery({
    queryKey: ['account-buckets', companyId],
    // Every page, not the server's default fifty. This page is the account,
    // and it was rendering 50 of 251 with no control to reach the rest.
    queryFn: ({ signal }) => backend.listAllAccountBuckets(companyId, undefined, signal),
    enabled: Boolean(companyId),
    retry,
    /**
     * No interval. Nothing on this page is outstanding.
     *
     * This listing changes when a discovery sweep observes the account, which
     * is not work this page started and not work it can see finish - so a
     * thirty-second poll here is the pure form of the defect the audit names:
     * an idle tab asking forever about something that will look the same. A
     * registration invalidates this key on the write, and the header carries
     * the read's own age with a control to ask again.
     */
    refetchInterval: false,
  });
  const registeredScopes = useQuery({
    queryKey: ['live-buckets'],
    queryFn: ({ signal }) => backend.listAllBuckets(signal),
    retry,
    staleTime: 30_000,
  });
  const scopeByName = useMemo(
    () => new Map((registeredScopes.data?.items ?? []).map((r) => [r.bucket.name, r.bucket.id])),
    [registeredScopes.data],
  );

  /**
   * The account being looked at, or null if genuinely unknown.
   *
   * The listing is the better authority and did not carry it: the response
   * described the buckets and not the account they were seen in, so with no
   * prior registration the product had nothing to state and nothing to send.
   * The contract now returns it for exactly that reason, in those words - "so
   * a product with no prior registration can still state which account it is
   * looking at rather than carrying a hardcoded one". Registrations remain the
   * fallback for a listing that has not landed.
   */
  const accountId = useMemo(() => {
    const observedAccount = (observed.data?.items ?? [])
      .map((b) => (b as { accountId?: string }).accountId)
      .find((id): id is string => typeof id === 'string' && id.length > 0);
    return observedAccount ?? registeredScopes.data?.items[0]?.accountId ?? null;
  }, [observed.data, registeredScopes.data]);

  /**
   * S3's account listing carries no region - a region needs a per-bucket
   * location call - so every observed row reports null. Rather than making
   * that the operator's data-entry problem, the region already recorded on
   * this account's registered scopes is offered as the default. It stays
   * editable, because a bucket in another region would read nothing.
   */
  const accountRegion = useMemo(() => {
    const counts = new Map<string, number>();
    for (const r of registeredScopes.data?.items ?? []) {
      if (r.accountId === accountId && r.bucket.region) {
        counts.set(r.bucket.region, (counts.get(r.bucket.region) ?? 0) + 1);
      }
    }
    let best: string | null = null;
    for (const [region, n] of counts) if (!best || n > (counts.get(best) ?? 0)) best = region;
    return best;
    // accountId is read above, so it belongs here: without it, switching
    // accounts kept offering the previous account's region as the default.
  }, [registeredScopes.data, accountId]);

  const [q, setQ] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [regionFor, setRegionFor] = useState<Record<string, string>>({});
  /** Which rows have their folders open. A folder is a scope; see BucketFolders. */
  const [foldersFor, setFoldersFor] = useState<string[]>([]);
  const [note, setNote] = useState<string | null>(null);
  const qc = useQueryClient();
  const navigate = useNavigate();

  const register = async (name: string, observedRegion: string | null) => {
    // No companyId. The contract made it optional and the server mints an
    // owner for this bucket alone; sending the first grant this identity holds
    // is what filed three unrelated corpora under one company record.
    const scope = { name, region: observedRegion ?? regionFor[name]?.trim() ?? accountRegion ?? null, accountId };
    const refusal = whyNotRegistrable(scope);
    if (refusal) {
      setNote(refusal);
      return;
    }
    setBusy(name);
    setNote(null);
    try {
      const r = await backend.registerBucket(registerBody(scope), newIdempotencyKey());
      void qc.invalidateQueries({ queryKey: ['account-buckets'] });
      void qc.invalidateQueries({ queryKey: ['live-buckets'] });
      // The flow continues where the work continues: land on the new pipeline.
      navigate(`/buckets/${r.bucket.id}`);
    } catch (e) {
      setNote(e instanceof ApiError ? `${name}: ${e.message}` : `${name}: the API did not respond.`);
    } finally {
      setBusy(null);
    }
  };

  if (companies.isPending) return <Loading what="Reading the account" rows={6} />;

  /**
   * The listing needs a grant; registration does not.
   *
   * This whole page used to be replaced by an EmptyState reading "No company
   * context", whose description said registrations attach buckets to a
   * company. The contract dropped that rule, `companyId` is optional on
   * `RegisterBucketRequest`, so the page was refusing on a constraint that no
   * longer exists, and a fresh identity could never reach the one screen that
   * registers a first bucket. What is genuinely blocked is narrower and named
   * where it applies: `/account-buckets` still takes a required companyId.
   */
  const listingRefusal =
    accountListingRefusal(companyId) ??
    (companies.isError && companies.error instanceof ApiError
      ? (companies.error.reason ?? companies.error.message)
      : null);

  const items = (observed.data?.items ?? []).filter((b) => b.bucketName.toLowerCase().includes(q.toLowerCase()));
  const registered = items.filter((b) => b.registered).length;

  return (
    <div className="min-h-0 flex-1 animate-pop space-y-3 overflow-y-auto p-5">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-h2 text-ink">S3</h1>
        <span className="mono text-caption text-ink-3">
          {observed.data
            ? `${formatCount(observed.data.matchedCount)} buckets observed${accountId ? ` in account ${accountId}` : ''} · ${registered} registered${
                observed.data.truncated ? ' · more than this client will page' : ''
              }`
            : 'listing the account…'}
        </span>
      </div>

      {/* An inline ref callback is detached and re-attached on every render,
          so this fired focus() on every render: typing one character into a
          row's region field re-rendered the page and focus jumped back here,
          which left that field able to hold exactly one character. A stable
          ref focused once on mount does what that was reaching for, and
          autoFocus is refused by the accessibility rules for good reason. */}
      <input
        ref={search}
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search the account's buckets…"
        className="w-full rounded border border-line bg-panel-2 px-3 py-2 text-body text-ink outline-none placeholder:text-ink-4 focus:border-acc-2"
      />

      {/* The listing is not the only way in, and on a fresh estate it is not
          any way in: registration needs a name, an account, a region and a
          folder, none of which has to have been observed first. With an empty
          catalogue every other path on this page offers nothing to press. */}
      <RegisterScope
        accountId={accountId}
        region={accountRegion}
        existing={(registeredScopes.data?.items ?? []).map((r) => ({
          name: r.bucket.name,
          prefix: (r as { prefix?: string }).prefix ?? '',
        }))}
      />

      {/* A registration that failed appears after the page has settled, so it
          is announced rather than quietly drawn, the same rule `ReadFailed`
          follows for a failed read. */}
      {note && (
        <p role="alert" className="text-small text-warn">
          {note}
        </p>
      )}

      <Panel>
        <PanelHeader
          title="Observed buckets"
          sub={
            accountRegion
              ? `open Folders to register one folder as its own scope, or Register for the whole bucket · S3 reports no region when listing an account, so ${accountRegion} is offered and stays editable per row`
              : "open Folders to register one folder as its own scope, or Register for the whole bucket · observation grants nothing; registration + read roles do"
          }
          actions={
            observed.data ? (
              <>
                <span className="text-caption text-ink-3">read {formatRelative(new Date(observed.dataUpdatedAt).toISOString())}</span>
                <Button variant="ghost" loading={observed.isFetching} onClick={() => void observed.refetch()}>
                  {observed.isFetching ? 'Reading…' : 'Read the account again'}
                </Button>
              </>
            ) : null
          }
        />
        {listingRefusal ? (
          <p className="px-4 py-4 text-small text-ink-3">{listingRefusal}</p>
        ) : observed.isPending ? (
          <div className="space-y-2 p-4">{Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-8 w-full" />)}</div>
        ) : observed.isError ? (
          <ReadFailed what={explainLine(observed.error, 'list the account')} onRetry={() => void observed.refetch()} />
        ) : items.length === 0 ? (
          <p className="px-4 py-4 text-small text-ink-3">No observed bucket matches “{q}”.</p>
        ) : (
          items.map((b) => {
            const gone = Boolean(b.lastMissingAt);
            const scopeId = scopeByName.get(b.bucketName);
            const showFolders = foldersFor.includes(b.bucketName);
            return (
              <div key={b.bucketName}>
              <div className={'flex items-center gap-3 border-b border-line px-4 py-2 text-small last:border-0 ' + (gone ? 'opacity-50' : '')}>
                <span className="mono min-w-0 flex-1 truncate text-ink">{b.bucketName}</span>
                {b.bucketRegion ? (
                  <span className="mono flex-none text-caption text-ink-3">{b.bucketRegion}</span>
                ) : !b.registered && !gone ? (
                  <input
                    placeholder="region"
                    aria-label={`Region for ${b.bucketName}`}
                    title={
                      accountRegion
                        ? `Defaulted to ${accountRegion}, the region this account's registered scopes use. Change it if this bucket lives elsewhere.`
                        : 'The account listing carries no region; type this bucket’s region.'
                    }
                    value={regionFor[b.bucketName] ?? accountRegion ?? ''}
                    onChange={(e) => setRegionFor((m) => ({ ...m, [b.bucketName]: e.target.value }))}
                    className="mono w-28 flex-none rounded border border-line bg-panel-2 px-2 py-0.5 text-caption text-ink-3 focus:text-ink"
                  />
                ) : null}
                {gone && <Stamp tone="mut">GONE FROM LISTING</Stamp>}
                {/* A scope is (account, bucket, prefix), and this is where a
                    person chooses the prefix. It was a bare chevron at the head
                    of the row: the control existed, worked, and read as
                    decoration next to a labelled Register, so the feature was
                    there and nobody could find it. It says what it does now. */}
                {!gone && (
                  <button
                    type="button"
                    onClick={() =>
                      setFoldersFor((xs) =>
                        xs.includes(b.bucketName) ? xs.filter((x) => x !== b.bucketName) : [...xs, b.bucketName],
                      )
                    }
                    aria-expanded={showFolders}
                    title={`Folders in ${b.bucketName}, each registrable as its own scope`}
                    className="flex flex-none items-center gap-1 rounded border border-line bg-panel-2 px-2.5 py-0.5 text-caption text-ink-2 transition-colors duration-fast hover:bg-hov hover:text-ink"
                  >
                    <ChevronRight
                      className={`h-3 w-3 transition-transform duration-fast ${showFolders ? 'rotate-90' : ''}`}
                      aria-hidden="true"
                    />
                    {showFolders ? 'Hide folders' : 'Folders'}
                  </button>
                )}
                {b.registered ? (
                  <>
                    <Stamp tone="ok">REGISTERED</Stamp>
                    {scopeId && (
                      <Link to={`/buckets/${scopeId}/graph`} className="rounded border border-line bg-panel-2 px-2 py-0.5 text-caption text-acc-2 transition-colors duration-fast hover:bg-hov">
                        Graph
                      </Link>
                    )}
                  </>
                ) : !gone ? (
                  <button
                    onClick={() => void register(b.bucketName, b.bucketRegion ?? null)}
                    disabled={busy === b.bucketName}
                    className="rounded border border-line bg-panel-2 px-2.5 py-0.5 text-caption text-acc-2 transition-colors duration-fast hover:bg-hov disabled:opacity-50"
                  >
                    {busy === b.bucketName ? 'Registering…' : 'Register'}
                  </button>
                ) : null}
              </div>
              {showFolders && companyId && (
                <BucketFolders
                  bucketName={b.bucketName}
                  companyId={companyId}
                  accountId={accountId}
                  region={b.bucketRegion ?? regionFor[b.bucketName]?.trim() ?? accountRegion ?? null}
                  existing={(registeredScopes.data?.items ?? []).map((r) => ({
                    name: r.bucket.name,
                    prefix: (r as { prefix?: string }).prefix ?? '',
                  }))}
                />
              )}
              </div>
            );
          })
        )}
      </Panel>
    </div>
  );
}
